import _ from "lodash";
import {
  ConsentManagementVariant,
  CustomConsentManagementVariant,
} from "../../../../common/service/server/dto/CMPSettingsDto";

const FLORIDA_REGION = "US-FL";

// Left out when comparing environments: the preview page is expected to differ, and the rest are legacy fields the
// form doesn't show.
const FIELDS_NOT_COMPARED_ACROSS_ENVIRONMENTS = new Set<string>([
  "previewURL",
  "usConsentManagementVariant",
  "informationalConsentManagementVariant",
  "gdprConsentManagementEnabled",
]);

// Treat the different ways a record can store "nothing" as the same. A region that was never set and one set to
// "None" both show no banner; records created by a publish store "None" where older ones have nothing. Lists are
// compared regardless of order (e.g. the order consent-choice banner types were ticked in).
const normalizeSettingValue = (value: unknown) => {
  if (
    value === undefined ||
    value === "" ||
    value === ConsentManagementVariant.NONE ||
    (Array.isArray(value) && value.length === 0)
  ) {
    return null;
  }
  if (Array.isArray(value)) {
    return [...value].sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b)));
  }
  return value;
};

// Florida has its own form field, so leave it out of the custom state list to avoid counting it twice.
const withoutFloridaOverride = (values: Record<string, unknown>) => ({
  ...values,
  customConsentManagementVariants: (
    (values.customConsentManagementVariants as CustomConsentManagementVariant[] | undefined) ?? []
  ).filter((variant) => variant.region !== FLORIDA_REGION),
});

/** The fields whose values differ between two environments' saved consent banner form values. */
export const differingSettings = (
  valuesA: Record<string, unknown>,
  valuesB: Record<string, unknown>,
): string[] => {
  const a = withoutFloridaOverride(valuesA);
  const b = withoutFloridaOverride(valuesB);
  const fields = new Set([...Object.keys(a), ...Object.keys(b)]);

  return Array.from(fields).filter(
    (field) =>
      !FIELDS_NOT_COMPARED_ACROSS_ENVIRONMENTS.has(field) &&
      !_.isEqual(normalizeSettingValue(a[field]), normalizeSettingValue(b[field])),
  );
};

/** How many settings differ between two environments' saved consent banner form values. */
export const countDifferingSettings = (
  valuesA: Record<string, unknown>,
  valuesB: Record<string, unknown>,
): number => differingSettings(valuesA, valuesB).length;
